import React, { useCallback, useEffect, useState } from 'react';

import { App, Button, Form, Input, Modal, Result, Spin, Steps, Switch, Tooltip, Typography } from 'antd';

import { useCreateConnect } from 'v2/hooks/useCreateConnect';
import { useListAutoresponders } from 'v2/hooks/useListAutoresponders';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { Icon } from '@eduzz/ui-antd-theme';

import { AUTORESPONDERS_WITH_CREATION_DISABLED, EStep } from './constants';
import Validations from './validations';
import { ConnectPlatform } from '../../constants';
import { useConnectState } from '../../contexts/ConnectStateContext';

const { RD_STATION, ACTIVE_CAMPAIGN, LEAD_LOVERS, BLING } = ConnectPlatform;

const STEP_ITEMS = [{ title: 'Descrição' }, { title: 'Plataforma' }, { title: 'Configuração' }];

const NewConnect: React.FC = () => {
  const { message } = App.useApp();
  const {
    newConnect,
    step,
    modalNewConnect: modal,
    nextStep,
    previousStep: handleBack,
    setStep,
    setNewConnect,
    closeNewConnectModal
  } = useConnectState();
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  const { autoresponders, isLoading: isLoadingAutoresponders } = useListAutoresponders();
  const createConnectMutation = useCreateConnect();

  const errorFor = useCallback(
    (field: string) => (showErrors ? validationErrors[field] || '' : ''),
    [showErrors, validationErrors]
  );
  const hasError = useCallback(
    (field: string) => showErrors && !!validationErrors[field],
    [showErrors, validationErrors]
  );

  const handleChange = useCallback(
    (field: string, value: unknown) => {
      setNewConnect({ ...newConnect, [field]: value });
    },
    [newConnect, setNewConnect]
  );

  const isValidToNextStep = useCallback(() => {
    const hasAutoresponderParams = Array.isArray(newConnect.autoresponder?.parameters?.parametros);
    const autoresponderParams: any[] = hasAutoresponderParams ? newConnect.autoresponder?.parameters?.parametros : [];

    switch (step) {
      case EStep.PLATFORM:
        return !!newConnect.description?.trim();
      case EStep.ACCOUNT:
        return !!newConnect.autoresponder;
      case EStep.CONNECT:
        return autoresponderParams.every((param: any) => !!newConnect[param.nome]);
      default:
        return false;
    }
  }, [newConnect, step]);

  useEffect(() => {
    if (newConnect) {
      setValidationErrors(run(newConnect, Validations) as Record<string, string>);
    }
  }, [newConnect]);

  useEffect(() => {
    if (!modal) {
      setShowErrors(false);
    }
  }, [modal]);

  const closeModal = useCallback(() => {
    setShowErrors(false);
    closeNewConnectModal();
  }, [closeNewConnectModal]);

  const handleNext = useCallback(() => {
    if (step === EStep.PLATFORM && !newConnect.description?.trim()) {
      setShowErrors(true);
      return;
    }

    if (step === EStep.ACCOUNT && !newConnect.autoresponder) {
      message.warning('Selecione uma plataforma para continuar.');
      return;
    }

    setShowErrors(false);
    nextStep();
  }, [step, newConnect, nextStep, message]);

  const handleSaveNewConnect = useCallback(async () => {
    if (!isValidToNextStep()) {
      message.warning('Preencha todos os campos obrigatórios.');
      return;
    }

    try {
      setStep(EStep.LOADING);
      const connectData = { ...newConnect };
      connectData.autoresponder_id = connectData.autoresponder.id;
      const fields: Record<string, any> = {};

      if (
        connectData.autoresponder &&
        connectData.autoresponder.parameters &&
        Array.isArray(connectData.autoresponder.parameters.parametros)
      ) {
        connectData.autoresponder.parameters.parametros.forEach((param: any) => {
          fields[param.nome] = connectData[param.nome];
          delete connectData[param.nome];
        });
      }

      connectData.fields = fields;
      delete connectData.autoresponder;
      await createConnectMutation.mutateAsync(connectData);
      setStep(EStep.DONE);
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Não foi possível salvar o connect.');
      setStep(EStep.CONNECT);
    }
  }, [newConnect, createConnectMutation, setStep, message, isValidToNextStep]);

  const redirectUrl = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const paramType = useCallback(
    (param: any) => {
      localStorage.setItem('connect-description', newConnect.description);

      if (param.tipo === 'field') {
        return (
          <Form.Item key={`parameter-${param.nome}`} label={param.label} className='mb-0' required>
            <Input
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(param.nome, e.target.value)}
              value={newConnect[param.nome]}
              placeholder={param.label}
            />
          </Form.Item>
        );
      }

      if (param.tipo === 'botao') {
        return (
          <Button
            key={`parameter-${param.nome}`}
            type='primary'
            block
            onClick={() => redirectUrl(`${param.botao_url}`)}
          >
            {param.botao_titulo}
          </Button>
        );
      }

      return null;
    },
    [newConnect, handleChange, redirectUrl]
  );

  const platformOptions =
    Array.isArray(autoresponders) &&
    autoresponders.filter(
      autoresponder =>
        autoresponder.id !== RD_STATION && !AUTORESPONDERS_WITH_CREATION_DISABLED.includes(autoresponder.id)
    );

  const isLastConfigStep = step === EStep.CONNECT;
  const showFormFooter = step < EStep.LOADING;

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      width={680}
      destroyOnClose
      title='Cadastro de Integração'
      footer={
        showFormFooter ? (
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={closeModal}>
              Cancelar
            </Button>
            {step !== EStep.PLATFORM && (
              <Button type='text' onClick={handleBack}>
                Voltar
              </Button>
            )}
            <Button
              type='primary'
              onClick={isLastConfigStep ? handleSaveNewConnect : handleNext}
              disabled={!isValidToNextStep()}
            >
              {isLastConfigStep ? 'Salvar integração' : 'Avançar'}
            </Button>
          </div>
        ) : step === EStep.DONE ? (
          <div className='flex justify-end'>
            <Button type='primary' onClick={closeModal}>
              Concluir
            </Button>
          </div>
        ) : null
      }
    >
      {step <= EStep.CONNECT && (
        <Steps current={step} className='mb-6' size='small' items={STEP_ITEMS} responsive={false} />
      )}

      {step === EStep.PLATFORM && (
        <Form layout='vertical' className='flex flex-col gap-4'>
          <div className='flex items-center justify-between rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
            <div className='flex flex-col'>
              <Typography.Text strong>Status da integração</Typography.Text>
              <Typography.Text type='secondary' className='text-sm'>
                {newConnect.active ? 'A integração ficará ativa após o cadastro' : 'A integração será criada inativa'}
              </Typography.Text>
            </div>
            <div className='flex items-center gap-2'>
              <Typography.Text type='secondary'>{newConnect.active ? 'Ativo' : 'Inativo'}</Typography.Text>
              <Switch checked={newConnect.active} onChange={checked => handleChange('active', checked)} />
            </div>
          </div>

          <Form.Item
            label='Descrição'
            required
            validateStatus={hasError('description') ? 'error' : undefined}
            help={
              hasError('description') ? errorFor('description') : 'Identifique esta integração para encontrá-la depois'
            }
            className='mb-0'
          >
            <Input.TextArea
              rows={4}
              placeholder='Ex.: Automações de vendas ActiveCampaign'
              value={newConnect.description}
              onChange={e => handleChange('description', e.target.value)}
              maxLength={120}
              showCount
            />
          </Form.Item>
        </Form>
      )}

      {step === EStep.ACCOUNT && (
        <div className='flex flex-col gap-4'>
          <div>
            <Typography.Text strong className='block'>
              Escolha a plataforma
            </Typography.Text>
            <Typography.Text type='secondary'>Selecione o provedor com o qual deseja integrar</Typography.Text>
          </div>

          {isLoadingAutoresponders ? (
            <div className='flex justify-center py-10'>
              <Spin />
            </div>
          ) : (
            <div className='grid max-h-80 grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3'>
              {platformOptions &&
                platformOptions.map((item: any) => (
                  <ListResponders key={item.id} item={item} newConnect={newConnect} handleChange={handleChange} />
                ))}
            </div>
          )}

          {newConnect.autoresponder && (
            <div className='flex items-center gap-3 rounded-(--uizz-antd-border-radius) border border-(--uizz-antd-color-primary) bg-(--uizz-antd-color-primary-bg) px-3 py-2'>
              <Icon.Check size={16} className='text-(--uizz-antd-color-primary)' />
              <Typography.Text>
                Plataforma selecionada: <Typography.Text strong>{newConnect.autoresponder.name}</Typography.Text>
              </Typography.Text>
            </div>
          )}
        </div>
      )}

      {step === EStep.CONNECT && (
        <div className='flex flex-col gap-5'>
          <div className='flex items-center gap-3 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) p-4'>
            {newConnect.autoresponder?.image && (
              <img
                src={newConnect.autoresponder.image}
                alt={newConnect.autoresponder?.name || 'Plataforma'}
                className='h-12 max-w-24 object-contain'
              />
            )}
            <div className='min-w-0'>
              <Typography.Text strong className='block'>
                {newConnect.autoresponder?.name}
              </Typography.Text>
              <Typography.Text type='secondary' className='block truncate'>
                {newConnect.description}
              </Typography.Text>
            </div>
          </div>

          <Form layout='vertical'>
            <div className='grid grid-cols-1 gap-4'>
              {Array.isArray(newConnect.autoresponder?.parameters?.parametros) &&
                newConnect.autoresponder.parameters.parametros.map((param: any) => paramType(param))}
            </div>
          </Form>
        </div>
      )}

      {step === EStep.LOADING && (
        <div className='flex flex-col items-center justify-center gap-3 py-12'>
          <Spin size='large' />
          <Typography.Text type='secondary'>Salvando sua integração...</Typography.Text>
        </div>
      )}

      {step === EStep.DONE && (
        <Result
          status='success'
          title='Integração criada com sucesso'
          subTitle='Agora você pode configurar as ações dessa integração.'
        />
      )}
    </Modal>
  );
};

const ListResponders = ({
  item,
  newConnect,
  handleChange
}: {
  item: any;
  newConnect: any;
  handleChange: (name: string, item: any) => void;
}) => {
  const isBeta = eduzzAccountsService.verifyUserHasBetaModule('new_connect');
  const betaProviders = [ACTIVE_CAMPAIGN, LEAD_LOVERS];
  const hasProviderInBeta = isBeta && betaProviders.includes(item.id);
  const hasInactiveConnect = [BLING].includes(item.id);

  const isDisabled = hasProviderInBeta || hasInactiveConnect;
  const tooltipTitle = hasProviderInBeta
    ? 'Esta plataforma foi migrada para a nova versão do Connect Eduzz, acesse o novo connect para configurá-lo'
    : hasInactiveConnect
      ? 'Essa plataforma foi descontinuada, utilize nossa integração nativa com o Zapier'
      : '';

  const isSelected = newConnect.autoresponder?.name === item.name;

  const button = (
    <button
      type='button'
      className={`flex h-28 w-full flex-col items-center justify-center gap-2 rounded-(--uizz-antd-border-radius-lg) border p-3 transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:grayscale ${
        isSelected
          ? 'border-(--uizz-antd-color-primary) bg-(--uizz-antd-color-primary-bg) shadow-sm'
          : 'border-(--uizz-antd-color-border) bg-(--uizz-antd-color-bg-container) hover:border-(--uizz-antd-color-primary-border-hover)'
      }`}
      onClick={() => handleChange('autoresponder', item)}
      disabled={isDisabled}
      aria-label={item.name}
      aria-pressed={isSelected}
    >
      <img src={item.image} alt={item.name} className='h-12 max-w-full object-contain' />
      <Typography.Text className='line-clamp-2 text-center text-xs'>{item.name}</Typography.Text>
    </button>
  );

  if (isDisabled) {
    return (
      <Tooltip placement='top' title={tooltipTitle}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default NewConnect;
